import { useCallback, useEffect, useRef, useState } from 'react';

const WARNING_THRESHOLD_MS = 20_000;

type UseInactivityRedirectOptions = {
  timeoutMs: number;
  onTimeout: () => void;
};

type UseInactivityRedirectResult = {
  isWarningVisible: boolean;
  remainingSeconds: number;
  cancelRedirect: () => void;
};

/**
 * Redireciona após `timeoutMs` fixos a partir do mount, sem reset por interação, avisando nos
 * últimos 20s. Expõe `cancelRedirect` para que o usuário possa desligar o redirecionamento com
 * uma ação simples (WCAG 2.2.1 — Timing Adjustable).
 */
export function useInactivityRedirect({
  timeoutMs,
  onTimeout,
}: UseInactivityRedirectOptions): UseInactivityRedirectResult {
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(Math.ceil(WARNING_THRESHOLD_MS / 1000));
  const [isCancelled, setIsCancelled] = useState(false);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    if (isCancelled) {
      return;
    }

    const warningDelay = Math.max(timeoutMs - WARNING_THRESHOLD_MS, 0);
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const warningTimeoutId = setTimeout(() => {
      setIsWarningVisible(true);
      setRemainingSeconds(Math.ceil(WARNING_THRESHOLD_MS / 1000));
      intervalId = setInterval(() => {
        setRemainingSeconds((seconds) => Math.max(seconds - 1, 0));
      }, 1000);
    }, warningDelay);

    const redirectTimeoutId = setTimeout(() => {
      onTimeoutRef.current();
    }, timeoutMs);

    return () => {
      clearTimeout(warningTimeoutId);
      clearTimeout(redirectTimeoutId);
      clearInterval(intervalId);
    };
  }, [timeoutMs, isCancelled]);

  const cancelRedirect = useCallback(() => {
    setIsCancelled(true);
    setIsWarningVisible(false);
  }, []);

  return { isWarningVisible, remainingSeconds, cancelRedirect };
}
